import { Controller } from "@hotwired/stimulus"

// Handles pitch count stepper and price binding for pricing cards
export default class extends Controller {
  static targets = [
    "productId",
    "priceId",
    "countValue",
    "priceValue",
    "decreaseBtn",
    "increaseBtn",
    "select",
    "quantityValue",
    "ctaSubscribe",
    "ctaCancel",
    "ctaUpdate",
    "ctaResume"
  ]
  static values = {
    products: Array,
    index: { type: Number, default: 0 },
    currentPriceId: String,
    onGrace: Boolean
  }

  connect() {
    // Sort by count ascending to ensure proper stepping
    if (this.hasProductsValue) {
      this.productsValue = [...this.productsValue].sort((a, b) => a.count - b.count)
      if (this.productsValue.length === 0) return

      if (this.hasCurrentPriceIdValue) {
        const idx = this.productsValue.findIndex(p => String(p.price_id) === String(this.currentPriceIdValue))
        if (idx >= 0) this.indexValue = idx
      }

      // Clamp index to bounds (default to 0)
      this.indexValue = Math.min(Math.max(this.indexValue || 0, 0), this.productsValue.length - 1)
      this.render()
      this.bindKeys()
    }
  }

  bindKeys() {
    this.keyHandler = (e) => {
      if (e.key === 'ArrowUp') { this.increase(); e.preventDefault() }
      if (e.key === 'ArrowDown') { this.decrease(); e.preventDefault() }
    }
    this.element.addEventListener('keydown', this.keyHandler)
  }

  disconnect() {
    if (this.keyHandler) this.element.removeEventListener('keydown', this.keyHandler)
  }

  increase() {
    if (!this.hasProductsValue) return
    if (this.indexValue < this.productsValue.length - 1) {
      this.indexValue++
      this.render()
    }
  }

  decrease() {
    if (!this.hasProductsValue) return
    if (this.indexValue > 0) {
      this.indexValue--
      this.render()
    }
  }

  render() {
    if (!this.productsValue || this.productsValue.length === 0) return
    const p = this.productsValue[this.indexValue]
    if (!p) return

    if (this.hasCountValueTarget) this.countValueTarget.textContent = p.count
    if (this.hasQuantityValueTarget) this.quantityValueTarget.textContent = p.count
    if (this.hasPriceValueTarget) this.priceValueTarget.textContent = p.price
    if (this.hasProductIdTarget) this.productIdTarget.value = p.id
    if (this.hasPriceIdTarget) this.priceIdTarget.value = p.price_id

    // Disable buttons at edges
    if (this.hasDecreaseBtnTarget) this.decreaseBtnTarget.disabled = (this.indexValue === 0)
    if (this.hasIncreaseBtnTarget) this.increaseBtnTarget.disabled = (this.indexValue === this.productsValue.length - 1)

    // Keep dropdown in sync
    if (this.hasSelectTarget) this.selectTarget.value = String(p.id)

    this.updateCta(p)
  }

  selectChange(event) {
    if (!this.hasProductsValue) return
    const selectedId = event.target.value
    const idx = this.productsValue.findIndex(x => String(x.id) === String(selectedId))
    if (idx >= 0) {
      this.indexValue = idx
      this.render()
    }
  }

  updateCta(product) {
    const hasCancel = this.hasCtaCancelTarget
    const hasUpdate = this.hasCtaUpdateTarget
    const hasSubscribe = this.hasCtaSubscribeTarget

    if (!hasCancel && !hasUpdate && !hasSubscribe) return

    const activeMode = this.hasCurrentPriceIdValue
    const onGrace = this.hasOnGraceValue && this.onGraceValue === true

    if (activeMode) {
      const isCurrent = String(product.price_id) === String(this.currentPriceIdValue)

      if (hasSubscribe) this.ctaSubscribeTarget.classList.add('hidden')

      if (hasUpdate) {
        this.ctaUpdateTarget.classList.remove('hidden')
        const enabled = !isCurrent
        if (this.ctaUpdateTarget.tagName === 'A') {
          this.ctaUpdateTarget.setAttribute('aria-disabled', enabled ? 'false' : 'true')
          this.ctaUpdateTarget.classList.toggle('pointer-events-none', !enabled)
        } else {
          this.ctaUpdateTarget.disabled = !enabled
        }

        this.ctaUpdateTarget.classList.toggle('opacity-50', !enabled)
        this.ctaUpdateTarget.classList.toggle('cursor-not-allowed', !enabled)
        this.ctaUpdateTarget.classList.toggle('bg-primary-500', enabled)
        this.ctaUpdateTarget.classList.toggle('text-black', enabled)
        this.ctaUpdateTarget.classList.toggle('hover:bg-lime-300', enabled)
        this.ctaUpdateTarget.classList.toggle('text-white', !enabled)
        this.ctaUpdateTarget.classList.toggle('hover:bg-white/10', !enabled)
      }

      if (onGrace) {
        if (this.hasCtaResumeTarget) this.ctaResumeTarget.classList.toggle('hidden', !isCurrent)
        if (hasCancel) this.ctaCancelTarget.classList.add('hidden')
      } else {
        if (this.hasCtaResumeTarget) this.ctaResumeTarget.classList.add('hidden')
        if (hasCancel) this.ctaCancelTarget.classList.remove('hidden')
      }
    } else {
      if (hasSubscribe) this.ctaSubscribeTarget.classList.remove('hidden')
      if (hasCancel) this.ctaCancelTarget.classList.add('hidden')
      if (hasUpdate) this.ctaUpdateTarget.classList.add('hidden')
      if (this.hasCtaResumeTarget) this.ctaResumeTarget.classList.add('hidden')
    }
  }
}
